import { ILocation } from "@sp-crm/core";
import {
    HoveredMapEntity,
    MapMarkerId,
} from "components/community-search/community-map/types";
import { SearchResultsMap } from "components/map/search-results-map";
import { MapSearchResults, MapView } from "components/map/types";
import { usStateCentroid } from "components/map/us-state-centroids";
import {
    SearchUserProfileRequest,
    useSearchUserProfilesMapViewQuery,
} from "generated/graphql";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useMemo } from "react";
import { useRegionId } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

interface AgentDirectoryMapProps {
    hoveredAgent: null | HoveredMapEntity;
    searchParams: SearchUserProfileRequest;
    onExplicitBoundsChanged: (northWest: ILocation, southEast: ILocation) => void;
}

export const AgentDirectoryMap: React.FC<AgentDirectoryMapProps> = props => {
    const { hoveredAgent, searchParams, onExplicitBoundsChanged } = props;
    const [mapView, setMapView] = React.useState<MapView | null>(null);
    const regionId = useRegionId();
    const { settings } = useTenantSettings();
    const fallbackCenter = useMemo(
        () => usStateCentroid(settings.defaultState),
        [settings.defaultState],
    );

    const query = useSearchUserProfilesMapViewQuery(
        {
            params: { ...searchParams, regionId },
            mapView,
        },
        {
            keepPreviousData: true,
            enabled: !!mapView,
        },
    );

    const searchResults: MapSearchResults = useMemo(() => {
        if (!query.data) {
            return null;
        }

        return {
            entityClusters: query.data.searchUserProfilesMapView.userProfileClusters.map(
                (c: { lat: number; lng: number; count: number }) => ({
                    lat: c.lat,
                    lng: c.lng,
                    count: c.count,
                }),
            ),
            entityCoordinates:
                query.data.searchUserProfilesMapView.userProfileCoordinates.map(
                    (coordinate: {
                        id: string;
                        lat: number;
                        lng: number;
                        name?: string;
                    }) => ({
                        id: coordinate.id as MapMarkerId,
                        lat: coordinate.lat,
                        lng: coordinate.lng,
                        name: coordinate.name || "(no name)",
                        color: "violet",
                    }),
                ),
            searchCoordinates: query.data.searchUserProfilesMapView.coordinates,
            recommendedZoom: query.data.searchUserProfilesMapView.recommendedZoom,
        };
    }, [query.data]);

    return (
        <SearchResultsMap
            explicitBounds={searchParams.geoParams?.bounds || null}
            fallbackCenter={fallbackCenter}
            onExplicitBoundsChanged={onExplicitBoundsChanged}
            hoveredEntity={hoveredAgent}
            isLoading={query.isLoading}
            basemapPolicy="allowThirdPartyFallback"
            surface="agent-directory"
            onMapViewChanged={setMapView}
            results={searchResults}
            error={query.error}
        />
    );
};
